import React, { useContext } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useAxisTooltip, useMouseTracker } from '@mui/x-charts/ChartsTooltip';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { generateVirtualElement } from '../../../../helpers/generateVirtualElement';
import { NO_VALUE_MARK } from '../../dashboardHelpers';
import { UserContext } from '../../../../context/UserContext';

interface MoreDataHintProps {
    message: string;
    theme: Theme;
}

const MoreDataHint = ({message, theme}: MoreDataHintProps) => 
<Box sx={{
    display: 'flex',
    padding: '10px',
    alignItems: 'center',
    borderBottom: `solid 1px`,
    borderColor: theme.palette.grey['12p'],
    backgroundColor: theme.palette.info['190p']
}}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <InfoOutlinedIcon />
        <Typography sx={{marginLeft:'8px'}} variant='body2'>{message}</Typography>
    </Box>
</Box>

const CustomAxisTooltip = () => {
    const theme = useTheme();
    const { locale } = useContext(UserContext);
     const { t, i18n } = useTranslation();

    const tooltipData = useAxisTooltip();
    const mousePosition = useMouseTracker();

    if (!tooltipData || !mousePosition || !tooltipData.identifier.x || !tooltipData.identifier.y) {
        return null;
    }

    const isMousePointer = mousePosition?.pointerType === 'mouse';
    const yOffset = isMousePointer ? 0 : 40 - mousePosition.height;

    const linearData = tooltipData.seriesItems.filter(el => el.seriesId !== 'barData')
        .sort((a, b) => {
            if (typeof b.value !== 'number' && typeof a.value !== 'number') {
                return 0
            }
            if (typeof b.value !== 'number') {
                return -1
            }
            if (typeof a.value !== 'number') {
                return 1
            }

            return b.value - a.value;
        })
    const barData = tooltipData.seriesItems.find(el => el.seriesId === 'barData')

    const hasMoreThanFiveData = linearData?.length >= 5;
    const hasMoreThanOneData = linearData?.length > 1 && linearData?.length < 5;

    return (
        <Popper
            sx={{
                width: 300,
                display: 'block',
                pointerEvents: 'none',
            }}
            open
            placement={isMousePointer ? 'top-end' : 'top'}
            anchorEl={generateVirtualElement(mousePosition)}
            modifiers={[{
                name: 'offset',
                options: { offset: [0, yOffset] },
            }]}
        >
            <Paper elevation={3} sx={{ borderRadius: '8px', boxShadow: '0 0 12px 4px rgba(0, 0, 0, 0.2)' }} >
                <Box>
                    <Box sx={{
                        display: 'flex',
                        padding: '10px',
                        alignItems: 'center',
                        borderBottom: `solid 1px`,
                        borderColor: theme.palette.grey['12p'],
                        backgroundColor: theme.palette.grey['4p'],
                    }}
                    >
                        <Box sx={{ width: '24px', height: '24px', marginRight: '8px' }}>
                            <EventOutlinedIcon sx={{ verticalAlign: 'middle' }} />
                        </Box>
                        <Box sx={{ width: '90%' }}>
                            <Typography style={theme.typography.tooltipText}>
                                {Intl.DateTimeFormat(locale,
                                    {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    }).format(new Date(tooltipData.axisValue))}
                            </Typography>
                        </Box>
                    </Box>
                    {linearData.slice(0, 5).map((dataItem) => (
                        <Box
                            key={`${dataItem?.formattedLabel}-${dataItem?.formattedValue}`}
                            sx={{ display: 'flex', padding: '10px' }}
                        >
                            <Box sx={{
                                width: '24px',
                                height: '24px',
                                marginRight: '8px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Box
                                    sx={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        bgcolor: dataItem.color,
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                                <Box sx={{ overflowWrap: 'anywhere' }}>
                                    <Typography style={theme.typography.tooltipText}>{
                                    i18n.exists(`screens.dashboarding.charts.${dataItem?.formattedLabel}`) ? t(`screens.dashboarding.charts.${dataItem?.formattedLabel}`) : dataItem?.formattedLabel }</Typography>
                                </Box>
                                <Box 
  sx={{ 
    textAlign: 'right',
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  }}
>
  <Typography 
    variant="body2"
    sx={{ 
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {dataItem?.formattedValue || NO_VALUE_MARK}
  </Typography>
</Box>
                            </Box>
                        </Box>
                    ))}
                    {barData &&
                        <Box sx={{ display: 'flex', padding: '10px', borderTop: `solid 1px`, borderColor: theme.palette.grey['12p'] }}>
                            <Box sx={{
                                width: '24px',
                                height: '24px',
                                marginRight: '8px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Box
                                    sx={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        bgcolor: barData?.color,
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                                <Box sx={{ overflowWrap: 'anywhere' }}>
                                    <Typography style={theme.typography.tooltipText}>{barData?.formattedLabel}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right', marginLeft: 'auto' }}>
                                    <Typography variant='body2'>
                                        {barData?.formattedValue === 'NaN' ? NO_VALUE_MARK : barData?.formattedValue}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    }
                    {hasMoreThanFiveData && <MoreDataHint message={t("screens.dashboarding.charts.showingMore5")} theme={theme} /> }
                    {hasMoreThanOneData && <MoreDataHint message={t("screens.dashboarding.charts.showingMore1")} theme={theme} /> }
                </Box>
            </Paper>
        </Popper>
    );
}

export default CustomAxisTooltip;
